import {
  AccountRecovery,
  type AccountRecoveryState,
} from "../account-recovery";
import { Alert, Button, Stack, Text } from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import type { RefObject } from "react";
import type { CredentialDialogState } from "./credential-state";
import { credentialDialogStyles as styles } from "./credential-dialog.styles";
import { CredentialFields } from "./credential-fields";

export function CredentialForm({
  state,
  disabled,
  id,
  input,
  onClose,
  onNameChange,
  onWriteChange,
  onSubmit,
  recovery,
  onRefresh,
}: {
  readonly state: Exclude<CredentialDialogState, { readonly kind: "secret" }>;
  readonly disabled: boolean;
  readonly id: string;
  readonly input: RefObject<HTMLInputElement | null>;
  readonly onClose: () => void;
  readonly onNameChange: (name: string) => void;
  readonly onWriteChange: (allowed: boolean) => void;
  readonly onSubmit: () => void;
  readonly recovery: AccountRecoveryState | undefined;
  readonly onRefresh: (() => void) | undefined;
}) {
  const pending = state.pending;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();

        if (!disabled && !pending && !recovery) onSubmit();
      }}
    >
      <Stack width="auto">
        {state.kind === "revoke" ? (
          <Text as="p">
            Revoke “{state.name}”? Applications using this credential will lose
            access.
          </Text>
        ) : (
          <CredentialFields
            state={state}
            disabled={disabled || pending}
            id={id}
            input={input}
            onNameChange={onNameChange}
            onWriteChange={onWriteChange}
          />
        )}
        {state.error ? (
          <Alert tone="danger" announcement="assertive">
            {state.error}
          </Alert>
        ) : null}
        {recovery ? (
          <Stack width="auto" gap={8}>
            <AccountRecovery
              recovery={recovery}
              resource="credentials"
              onRefresh={onRefresh}
            />
          </Stack>
        ) : null}
        {pending ? (
          <output>
            <Text>
              {state.kind === "revoke" ? "Revoking…" : "Creating…"} Please wait
              before closing.
            </Text>
          </output>
        ) : null}
        <div {...stylex.props(styles.actions)}>
          <Button type="button" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={state.kind === "revoke" ? "danger" : "primary"}
            disabled={
              pending ||
              disabled ||
              !!recovery ||
              (state.kind === "create" && !state.name.trim())
            }
          >
            {state.kind === "revoke"
              ? "Revoke credential"
              : "Create credential"}
          </Button>
        </div>
      </Stack>
    </form>
  );
}
